import axios from "axios";
import { omit } from "lodash";
import { EnvV1, Nft } from "../types";
import { IPFS_GATEWAY, NFT_ATTRIBUTES } from "./constants";

//Config
import { File } from "nft.storage";
import { Blob } from "buffer";
import config from "../config";

export const isFileTypeImage = ({ type }: File) => {
  const [key, value] = type.split("/");
  return [".png", ".jpeg", ".svg+xml", ".gif"].includes("." + value);
};

export const cleanNftData = (nft: Nft) => ({
  ...omit(nft, ["isUnlimited", "quantity", "id"]),
  attributes: nft.attributes
    ?.filter((attribute) => !attribute.private)
    .map((attribute) => ({ ...omit(attribute, ["file", "private", "id"]) })),
});

export const convertBlobToBase64 = async (blob: Blob | File) => {
  return (
    "data:" +
    blob.type +
    ";base64," +
    Buffer.from(await blob.arrayBuffer()).toString("base64")
  );
};

export const uploadFilesOnStorage = async ({
  blob,
  contractId,
  tokenId,
  workspaceId,
  storageType,
  env,
  authToken,
  fileName,
}: {
  blob: Blob;
  contractId: string;
  workspaceId: string;
  tokenId: string;
  storageType: string;
  env: EnvV1;
  authToken: string;
  fileName: string;
}) => {
  try {
    // const data = {
    //   nftId: tokenId,
    //   attributeId: attribute.trait_type + i.toString(),
    // };

    const response = await axios.post(
      `${config.V1[env].BACKEND_ENDPOINT}/api/uploadFile`,
      {
        file: {
          base64: await convertBlobToBase64(blob),
          name: fileName,
          mimeType: blob.type,
        },
        tokenId,
        contractId,
        storageType,
      },
      {
        headers: {
          // "Content-Type": "multipart/formdata",
          authorization: authToken,
          workspaceid: workspaceId,
        },
      }
    );

    return response.data;
  } catch (error) {
    console.log(error);
  }
};

/**
 * @description Function that takes in input Nft[] and handle the update of the attributes' images
 *
 * @param nfts
 * @param param1
 * @param workspaceId
 * @returns Nft[] with update attributes remove file and store image url in link field
 */
export const storeAttributedData = (
  nfts: Nft[],
  { name, contractId }: { name: string; contractId: string },
  workspaceId: string,
  env: EnvV1,
  authToken: string
) => {
  return nfts.map(async (nft, tokenIndex) => {
    if (nft.attributes) {
      return {
        ...nft,
        attributes: await Promise.all(
          nft.attributes.map(async (attribute, i) => {
            const { type, file, private: isPrivate, value, link } = attribute;

            if (NFT_ATTRIBUTES.includes(type)) {
              if (file) {
                const value = await uploadFilesOnStorage({
                  blob: (file.blob as unknown) as Blob,
                  contractId,
                  tokenId: `${name}${tokenIndex + 1}`,
                  workspaceId,
                  storageType: isPrivate ? "bcode" : "ipfs",
                  env,
                  authToken,
                  fileName: file.name,
                });

                return {
                  ...omit(attribute, ["file", "link"]),
                  value:
                    type === "partner"
                      ? value
                      : isPrivate
                      ? value
                      : `${IPFS_GATEWAY}/${value}`,
                  link: isPrivate ? value : `${IPFS_GATEWAY}/${value}`,
                };
              }
              return {
                ...omit(attribute, ["file"]),
                value: type === "partner" ? value : link || value,
              };
            }
            return omit(attribute, ["file", "link"]);
          })
        ),
      };
    }
    return nft;
  });
};

/**
 * @description Function to upload NFT token's metadata on IPFS
 *
 * @param nfts
 * @param contractId
 * @param workspaceId
 * @returns {metadataCid, imagesCid}
 */
export async function uploadNftsIpfs(
  nfts: Nft[],
  contractId: string,
  workspaceId: string,
  env: EnvV1,
  authToken: string
) {
  const newNfts: { files: string[]; metadatas: object[] } = {
    files: [],
    metadatas: [],
  };
  for (const i in nfts) {
    const { image, ...metadata } = cleanNftData(nfts[i]);
    if (typeof image !== "string") {
      newNfts.files.push(await convertBlobToBase64(image.blob as Blob));
    } else {
      newNfts.files.push(image);
    }
    newNfts.metadatas.push({ ...metadata, image });
  }

  const response = await axios.post(
    `${config.V1[env].BACKEND_ENDPOINT}/api/uploadMetadataToIpfs`,
    newNfts,
    {
      headers: {
        authorization: authToken,
        contractId,
        workspaceId,
      },
    }
  );

  return response.data.message as { metadataCid: string; imagesCid: string };
}
